import { memo, useCallback, useState } from 'react'
import { Modal } from 'components/layouts/modal/modal'
import { useTranslation } from '@crew/modules/i18n'
import { apiBaseUrl } from '@crew/configs/constants'
import { FileUploader } from 'devextreme-react'
import classNames from 'classnames'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useToast } from 'hooks/useToast'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useTenantUserBulkUpdateDialog } from './useTenantUserBulkUpdateDialog'
import { BulkUpdateUserTable } from './components/bulkUpdateUserTable/bulkUpdateUserTable'
import { VerifyBulkUpdateUsersResponse } from '@crew/apis/user/models/verifyBulkUpdateUsers/response'
import {
  buildApiUrl,
  downloadBlob,
  extractFileNameFromContentDisposition,
} from '@crew/utils'
import { axiosInstance } from 'apis/axiosInstance'

export type PropsMode = {
  title: string
  isOpen: boolean
  onClose: () => void
}

export const TenantUserBulkUpdateDialog = memo((props: PropsMode) => {
  const { t } = useTranslation()
  const toast = useToast()

  const { verifyBulkUpdateUsers, bulkUpdateUsers, isLoadingBulkUpdateUsers } =
    useTenantUserBulkUpdateDialog()

  const [fileName, setFileName] = useState<string>()
  const [showApiErrors] = useShowApiErrors()

  // 更新内容の検証結果
  const [verifyBulkUpdateUsersResult, setVerifyBulkUpdateUsersResult] =
    useState<VerifyBulkUpdateUsersResponse>()

  // ユーザーCSVファイルのダウンロード
  const handleDownloadUsersCsv = useCallback(async () => {
    // RTK Queryではなく、axiosInstanceを使用し、fetchを行う
    // RTKQueryは通常JSONレスポンスを想定しており、Blob（バイナリデータ）を適切にシリアライズできないため
    // 開発者ツールのコンソールを見ると、エラー（A non-serializable value was detected.～）が出ている
    const url = buildApiUrl(apiBaseUrl(), `api/v1/users/download-csv`)

    // TODO: 呼び出し側でaxiosInstanceを直接使用しないように、以下タスクでリファクタ予定
    // https://break-tmc.atlassian.net/browse/CREW-16919
    const response = await axiosInstance.get(url.toString(), {
      responseType: 'blob',
    })
    const blob = response.data
    const contentDisposition = response.headers['content-disposition']

    const fileName =
      extractFileNameFromContentDisposition(contentDisposition) || 'users.csv'

    // Blobを使用してダウンロードを実行
    downloadBlob(blob, fileName)
  }, [])

  // ファイルアップロード
  const handleFileUpload = useCallback(
    async (file: File) => {
      setFileName(file.name)
      try {
        // ユーザー更新内容の検証
        const result = await verifyBulkUpdateUsers(file)

        setVerifyBulkUpdateUsersResult(result)
      } catch (error) {
        setVerifyBulkUpdateUsersResult(undefined)
        showApiErrors(error)
      }
    },
    [showApiErrors, verifyBulkUpdateUsers]
  )

  // 閉じるボタン押下時
  const handleCloseDialog = useCallback(() => {
    // ダイアログを閉じる
    props.onClose()
    // 状態を初期化
    setFileName(undefined)
    setVerifyBulkUpdateUsersResult(undefined)
  }, [props])

  // 「ユーザーを更新」ボタン押下時
  const handleUpdateUsersButtonClick = useCallback(async () => {
    try {
      if (!verifyBulkUpdateUsersResult) return

      // ユーザーを一括更新
      await bulkUpdateUsers(verifyBulkUpdateUsersResult)

      // ダイアログを閉じる
      handleCloseDialog()

      toast.success(t('message.tenant.bulkUpdateUsersSuccess'))
    } catch (error) {
      showApiErrors(error)
    }
  }, [
    bulkUpdateUsers,
    handleCloseDialog,
    showApiErrors,
    t,
    toast,
    verifyBulkUpdateUsersResult,
  ])

  return (
    <Modal
      title={props.title}
      isOpen={props.isOpen}
      onClose={handleCloseDialog}
    >
      {/* モーダルの最小幅を制限し、画面の各入力項目が正しく表示されるようにする */}
      <div className="p-2.5 flex flex-col gap-y-2.5 min-w-[800px]">
        <div>
          <p>{t('label.bulkUpdateDescription.line1')}</p>
          <p>{t('label.bulkUpdateDescription.line2')}</p>
        </div>

        {/* 現在のユーザーをCSVファイルに保存する */}
        <p className="crew-link" onClick={handleDownloadUsersCsv}>
          {t('label.bulkUpdateDownloadUsersCsv')}
        </p>

        {/* 注釈 */}
        <div>
          <p>{t('label.csvTemplateNote1')}</p>
          <p>{t('label.csvTemplateNote2')}</p>
          <p>{t('label.csvTemplateNote3')}</p>
        </div>

        {/* ファイルドロップ領域 */}
        <div
          id="dropZone"
          className={classNames(
            'h-24 flex items-center justify-center',
            'border-2 border-dashed', // 線
            'bg-crew-gray-1-light dark:bg-crew-gray-4-dark' // 背景色
          )}
        >
          <p>{fileName ?? t('label.dragAndDropCsvFile')}</p>
        </div>

        {/* ファイルアップローダー */}
        <FileUploader
          dropZone="#dropZone"
          visible={false} //カスタムドロップ領域のみ表示するため、FileUploader本体は表示しない
          uploadMode="instantly"
          uploadFile={handleFileUpload}
          dialogTrigger="#dropZone"
          allowedFileExtensions={['.csv']}
          accept="text/csv"
        />

        {/* ユーザー一覧 */}
        <div className="flex-1 mt-3">
          {/* 更新対象のレコードがない場合はメッセージを表示 */}
          {verifyBulkUpdateUsersResult?.data.length === 0 ? (
            <p className="mb-1 text-sm crew-error-text">
              {t('message.tenant.noUpdateTarget')}
            </p>
          ) : null}

          {/* 更新内容の検証でエラー発生時、エラーメッセージを表示 */}
          {verifyBulkUpdateUsersResult?.errorCount &&
          verifyBulkUpdateUsersResult.errorCount > 0 ? (
            <p className="mb-1 text-sm crew-error-text">
              {t('message.tenant.batchUpdateError', {
                count: verifyBulkUpdateUsersResult.errorCount,
              })}
            </p>
          ) : null}

          <BulkUpdateUserTable
            updateUsers={verifyBulkUpdateUsersResult?.data ?? []}
          />
        </div>

        <div className="flex flex-row gap-x-2.5 justify-end">
          {/* ユーザーを更新 */}
          <CrewButton
            text={t('action.updateUser')}
            type="primary"
            onClick={handleUpdateUsersButtonClick}
            disabled={
              !verifyBulkUpdateUsersResult?.data ||
              verifyBulkUpdateUsersResult.data.length === 0 ||
              verifyBulkUpdateUsersResult.errorCount > 0 ||
              isLoadingBulkUpdateUsers
            }
          />

          {/* キャンセルボタン */}
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCloseDialog}
          />
        </div>
      </div>
    </Modal>
  )
})
